import {
	type StudentWithPoints,
	type BothFullMissionStates,
	type ClassStudentAnalytics,
	getTeacherResponseFromMission,
	getStudentsWithSurveysFromMission,
	shouldShowSequenceFrameFromMission,
	shouldShowSurveyFromMission,
	shouldShowGeneratingAnalyticsFromMission,
	getStudent,
	getStudents,
	getBasicPointsForStudent,
	sumPointsObject,
} from '@mission.io/mission-toolkit'
import { TOP_SCORES_MAX_STUDENTS } from '../../components/frames/EndSequenceFrame/constants'

type State = ?BothFullMissionStates

export type Commendations = {
	criticalThinking: Set<string>,
	creativity: Set<string>,
	communication: Set<string>,
	collaboration: Set<string>,
	leadership: Set<string>,
}

export type SurveyAnswers = {
	missionRating: number,
	missionImprovementSuggestions: string,
	technicalDifficulties: string,

	studentCommendations: Commendations,
	MVPs: Set<string>,
	missionResult: 'SUCCESS' | 'FAILURE' | 'NONE',
}

export type MVPStudent = {
	firstName: string,
	lastName: string,
	score: number,
	id: string,
}

export function getStudentsWithSurveys(state: State): Set<string> {
	return getStudentsWithSurveysFromMission(state)
}

export const shouldShowSequenceFrame =
	(analyticsAvailable: boolean): ((state: State) => boolean) =>
	(state: State) => {
		return shouldShowSequenceFrameFromMission(state, { analyticsAvailable })
	}

export function shouldShowSurvey(state: State): boolean {
	return shouldShowSurveyFromMission(state)
}

export const shouldShowGeneratingAnalytics =
	(analyticsAvailable: boolean): ((state: State) => boolean) =>
	(state: State) => {
		return shouldShowGeneratingAnalyticsFromMission(state, {
			analyticsAvailable,
		})
	}

/**
 * Returns a list of MVP student types, referencing the names of students chosen by the teacher as mvps.
 * @param {State} state
 */
export function getMVPs(state: State): MVPStudent[] {
	const mvpStudents: MVPStudent[] = []
	const teacherResponse = getTeacherResponseFromMission(state)
	if (!teacherResponse) return mvpStudents
	teacherResponse.MVPs.forEach(studentId => {
		const student = getStudent(state, studentId)
		if (!student) return
		mvpStudents.push(getStudentObject(student))
	})
	return mvpStudents
}

/**
 * This function maps a StudentPoints to an MVPStudent object
 * @param {StudentWithPoints} student
 */
function getStudentObject(student: StudentWithPoints): MVPStudent {
	return {
		firstName: student.firstName,
		lastName: student.lastName,
		score: student.points,
		id: student.id,
	}
}

/**
 * Returns an ordered list of the top 8 or less students with the highest scores
 * @param {State} state
 */
export function getTopScorers(
	state: State,
	missionAnalytics: ?ClassStudentAnalytics
): MVPStudent[] {
	const students = getStudents(state)
	// Only look at students who earned points
	const filteredStudents = []
	const analyticsCrewPoints = sumPointsObject(missionAnalytics?.extraCrewPoints)

	Object.keys(students).forEach(studentId => {
		const pointsFromFullState = getBasicPointsForStudent(state, studentId)

		const studentPoints =
			missionAnalytics?.studentAnalytics?.[studentId]?.studentPoints
		let pointsFromAnalytics: number =
			sumPointsObject(studentPoints) + analyticsCrewPoints

		const points =
			studentPoints != null ? pointsFromAnalytics : pointsFromFullState
		if (points) {
			filteredStudents.push(
				getStudentObject({ ...students[studentId], points })
			)
		}
	})
	const orderedScores = filteredStudents.sort((a, b) => b.score - a.score)
	if (orderedScores.length < TOP_SCORES_MAX_STUDENTS) return orderedScores
	return orderedScores.slice(0, TOP_SCORES_MAX_STUDENTS)
}
